import { useState } from "react";
import { Play, X } from "lucide-react";

const BASE = `${process.env.PUBLIC_URL}/intro`;
const MP4 = `${BASE}/rookie-radar-intro-720p.mp4`;
const POSTER = `${BASE}/rookie-radar-intro-poster.jpg`;
const VTT_FR = `${BASE}/rookie-radar-intro-fr.vtt`;
const VTT_EN = `${BASE}/rookie-radar-intro-en.vtt`;

// Analytique défensive : ne fait rien si aucun fournisseur n'est présent (Cloudflare Web Analytics
// est sans cookie et sans API d'événements). On émet aussi un CustomEvent, sans effet de bord.
function track(name) {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.plausible === "function") window.plausible(name);
    if (typeof window.gtag === "function") window.gtag("event", name);
    window.dispatchEvent(new CustomEvent("rr-analytics", { detail: { event: name } }));
  } catch (e) { /* no-op */ }
}

// Présentation vidéo (22 s, voix off) — visiteurs uniquement. Vignette 16:9 pleine largeur de
// colonne. Aucun autoplay ; la <video> n'est montée (et le .mp4 téléchargé) qu'au clic.
// Le « × » délègue à onDismiss : c'est Home qui ferme le bandeau ENTIER et mémorise le choix.
export default function IntroVideo({ lang, t, onDismiss }) {
  const [playing, setPlaying] = useState(false);

  const play = () => { setPlaying(true); track("intro_video_play"); };
  const dismiss = (e) => {
    e.stopPropagation();
    track("intro_video_dismiss");
    onDismiss?.();
  };
  const onTimeUpdate = (e) => {
    const v = e.currentTarget;
    if (v.duration && v.currentTime / v.duration >= 0.5 && !v.dataset.half) {
      v.dataset.half = "1";
      track("intro_video_50");
    }
  };

  return (
    <div className="relative w-full" data-testid="home-intro">
      <div className="relative overflow-hidden rounded-lg border border-[var(--wl-line)] bg-black" style={{ aspectRatio: "16 / 9" }}>
        {playing ? (
          <video className="h-full w-full object-contain" controls autoPlay playsInline preload="none"
            poster={POSTER} data-testid="home-intro-video" onTimeUpdate={onTimeUpdate} onEnded={() => track("intro_video_complete")}>
            <source src={MP4} type="video/mp4" />
            <track kind="subtitles" src={VTT_FR} srcLang="fr" label="Français" default={lang === "fr"} />
            <track kind="subtitles" src={VTT_EN} srcLang="en" label="English" />
          </video>
        ) : (
          <button type="button" onClick={play} aria-label={t("home.intro_aria")} data-testid="home-intro-play"
            className="group absolute inset-0 h-full w-full cursor-pointer">
            <img src={POSTER} alt={t("home.intro_poster_alt")} className="h-full w-full object-cover" data-testid="home-intro-poster" />
            <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--wl-accent)] text-black shadow-lg transition-transform duration-200 group-hover:scale-110">
                <Play className="h-6 w-6 translate-x-[1px]" fill="currentColor" />
              </span>
            </span>
            <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 wl-mono text-[10px] text-white" data-testid="home-intro-duration" aria-hidden="true">
              <Play className="h-2.5 w-2.5" fill="currentColor" /> {t("home.intro_duration")}
            </span>
          </button>
        )}
        <button type="button" onClick={dismiss} aria-label={t("home.intro_close")} data-testid="home-intro-dismiss"
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white/80 transition-colors hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
